import React, { useState } from 'react';
import { KYCDocumentUpload } from '../user/KYCDocumentUpload';
import { kycAdminService } from '../../lib/services/kycAdminService';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface AdminKYCCompletionProps {
  currentUser: any;
}

interface AdminUserSummary {
  id: number;
  username: string | null;
  email: string | null;
  full_name?: string | null;
}

const AdminKYCCompletion: React.FC<AdminKYCCompletionProps> = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AdminUserSummary[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const term = searchTerm.trim();
    if (!term) {
      setError('Enter username, email, or user ID to search');
      setSearchResults([]);
      setSelectedUser(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/kyc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'search_users', data: { searchTerm: term } })
      });
      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Error searching users for AdminKYCCompletion:', result.error);
        setError(result.error || 'Failed to search users');
        setSearchResults([]);
        setSelectedUser(null);
        return;
      }

      const results = (result.data || []) as AdminUserSummary[];
      setSearchResults(results);
      setSelectedUser(results.length === 1 ? results[0] : null);
    } catch (err: any) {
      console.error('❌ Unexpected error searching users:', err);
      setError(err.message || 'Unexpected error searching users');
      setSearchResults([]);
      setSelectedUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user: AdminUserSummary) => {
    setSelectedUser(user);
  };

  const handleKYCComplete = async (kycRecord: any) => {
    if (!currentUser?.email) {
      alert('Cannot finalize KYC: admin email not available.');
      return;
    }
    if (!kycRecord?.id || !selectedUser?.id) {
      console.error('❌ Missing KYC id or selected user:', { kycRecord, selectedUser });
      return;
    }

    try {
      const description =
        selectedUser.username ||
        selectedUser.email ||
        `User ID ${selectedUser.id}`;
      const comments = `Approved via Admin KYC Completion dashboard for ${description}`;
      const result = await kycAdminService.approveKYC(
        String(kycRecord.id),
        currentUser.email,
        comments
      );

      if (!result.success) {
        console.error('❌ Error auto-approving KYC from admin completion:', result.error);
        alert(
          `KYC submitted, but automatic approval failed: ${
            result.error || 'Unknown error'
          }`
        );
      } else {
        alert('KYC submitted and approved successfully.');
      }
    } catch (err: any) {
      console.error('❌ Unexpected error approving KYC from admin completion:', err);
      alert('KYC submitted, but automatic approval failed due to an unexpected error.');
    }
  };

  return (
    <div>
      <h3
        style={{
          fontSize: '20px',
          color: '#FBBF24',
          marginBottom: '12px',
          fontWeight: 600
        }}
      >
        📝 Admin KYC Completion
      </h3>
      <p style={{ color: '#ffffff', marginBottom: '12px', fontSize: '14px' }}>
        Search for a user and complete their KYC on their behalf using the same
        form and validation as the user-facing KYC flow.
      </p>

      <form
        onSubmit={handleSearch}
        style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}
      >
        <input
          type="text"
          placeholder="Search by user ID, username, or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid #374151',
            backgroundColor: '#111827',
            color: '#F9FAFB',
            fontSize: '14px'
          }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#2563EB',
            color: 'white',
            fontWeight: 600,
            cursor: 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && (
        <div
          style={{
            marginBottom: '8px',
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: 'rgba(248, 113, 113, 0.15)',
            color: '#FCA5A5',
            fontSize: '13px'
          }}
        >
          {error}
        </div>
      )}

      {searchResults.length > 0 && (
        <div
          style={{
            marginBottom: '12px',
            borderRadius: '8px',
            border: '1px solid #1F2937',
            backgroundColor: '#050816'
          }}
        >
          {searchResults.map((u) => {
            const isActive = selectedUser?.id === u.id;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => handleSelectUser(u)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  backgroundColor: isActive
                    ? 'rgba(37, 99, 235, 0.2)'
                    : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #111827',
                  color: '#E5E7EB',
                  cursor: 'pointer'
                }}
              >
                {u.username || u.email || `User #${u.id}`}
                {u.full_name && (
                  <span style={{ color: '#ffffff', fontSize: '14px' }}>
                    {' '}
                    · {u.full_name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selectedUser && (
        <div
          style={{
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid #1F2937'
          }}
        >
          <p style={{ color: '#ffffff', fontSize: '13px', marginBottom: '8px' }}>
            Completing KYC for{' '}
            <span style={{ color: '#F9FAFB', fontWeight: 500 }}>
              {selectedUser.username ||
                selectedUser.email ||
                `User #${selectedUser.id}`}
            </span>
            . After submission their KYC status will be set to completed and the
            action will be logged in the admin audit trail.
          </p>
          <KYCDocumentUpload
            userId={selectedUser.id}
            onComplete={handleKYCComplete}
            onCancel={() => setSelectedUser(null)}
          />
        </div>
      )}
    </div>
  );
};

export default AdminKYCCompletion;

